// frontend/src/hooks/useIOSViewportStabilizer.ts
// Custom hook to stabilize the viewport on iOS Safari and installed PWAs.
// Resolves unwanted zooming, layout jumping, and virtual keyboard layout gaps.

import { useEffect, useRef } from 'react';

export function useIOSViewportStabilizer(activeTab?: string) {
  const activeTabRef = useRef(activeTab);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  useEffect(() => {
    // Check if the user agent is iOS (iPhone/iPad/iPod)
    const isIOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) ||
      (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);

    if (!isIOS) return;

    // Helper: Reset viewport scaling programmatically
    const resetViewportScale = () => {
      const viewportMeta = document.querySelector('meta[name="viewport"]');
      if (viewportMeta) {
        // Set maximum-scale=1.0 and user-scalable=no temporarily to force-reset scale
        viewportMeta.setAttribute(
          'content',
          'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover'
        );

        // Restore original, accessible content (allowing pinch-to-zoom) after scale is stabilized
        setTimeout(() => {
          viewportMeta.setAttribute(
            'content',
            'width=device-width, initial-scale=1.0, viewport-fit=cover'
          );
        }, 100);
      }
    };

    // 1. Reset scale when navigation or tab changes
    resetViewportScale();

    // 2. Handle input blur/focusout to restore layout from keyboard open state
    const handleFocusOut = (e: FocusEvent) => {
      const target = e.target as HTMLElement | null;
      if (target && ['INPUT', 'TEXTAREA', 'SELECT'].includes(target.tagName)) {
        // Small delay to allow the dynamic virtual keyboard to start collapsing
        setTimeout(() => {
          const activeEl = document.activeElement;
          // Only trigger scroll adjustment if another input did not instantly gain focus
          if (!activeEl || !['INPUT', 'TEXTAREA', 'SELECT'].includes(activeEl.tagName)) {
            // Force a scroll recalculation by scrolling slightly.
            // This eliminates WebKit's blank space layout gaps at the bottom.
            window.scrollTo({
              top: window.scrollY || document.documentElement.scrollTop,
              left: window.scrollX || document.documentElement.scrollLeft,
              behavior: 'instant' as any // Using instant for layout realignment
            });

            // Clean up viewport scale zoom if WebKit zoomed in
            resetViewportScale();
          }
        }, 80);
      }
    };

    // 3. Reset viewport scale when app returns from background
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        resetViewportScale();
      }
    };

    const handlePageShow = (e: PageTransitionEvent) => {
      if (e.persisted) {
        resetViewportScale();
      }
    };

    // 4. Eliminate PWA Standalone double-tap zoom
    // Note: pinch-to-zoom is fully preserved for accessibility.
    let lastTouchEnd = 0;
    const preventDoubleTapZoom = (e: TouchEvent) => {
      const now = Date.now();
      if (now - lastTouchEnd <= 300) {
        e.preventDefault();
      }
      lastTouchEnd = now;
    };

    // Add event listeners
    document.addEventListener('focusout', handleFocusOut);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('pageshow', handlePageShow);
    
    // Add touch listener with passive: false to allow preventDefault()
    document.addEventListener('touchend', preventDoubleTapZoom, { passive: false });

    // 5. Handle VisualViewport changes (iOS dynamic keyboard sizing)
    const handleViewportResize = () => {
      if (window.visualViewport) {
        // Check if keyboard is closed (visual viewport matches window inner height)
        const isKeyboardClosed = Math.abs(window.visualViewport.height - window.innerHeight) < 20;
        if (isKeyboardClosed) {
          resetViewportScale();
        }
      }
    };

    if (window.visualViewport) {
      window.visualViewport.addEventListener('resize', handleViewportResize);
    }

    // Cleanup listeners
    return () => {
      document.removeEventListener('focusout', handleFocusOut);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('touchend', preventDoubleTapZoom);
      
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportResize);
      }
    };
  }, [activeTab]); // Triggers when the tab changes

  return null;
}
